import { useState, useEffect } from 'react';
import { Card, Button, Badge, Input, NumberInput } from '@atoms/index';
import { AlertModal } from '@organisms/index';
import { menuService } from '@services/menuService';
import { formatCurrency, parseDecimal } from '@/utils/currency';
import { cn } from '@/utils/cn';
import type { AlertModalVariant } from './AlertModal';

interface OptionGroupManagementProps {
  productId: string;
  onUpdate?: () => void;
}

export const OptionGroupManagement = ({
  productId,
  onUpdate,
}: OptionGroupManagementProps) => {
  const [optionGroups, setOptionGroups] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  const [editingOptionId, setEditingOptionId] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showOptionForm, setShowOptionForm] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [alertState, setAlertState] = useState<{ isOpen: boolean; title: string; message: string; variant: AlertModalVariant }>({ isOpen: false, title: '', message: '', variant: 'warning' });

  useEffect(() => {
    if (productId) {
      loadOptionGroups();
    }
  }, [productId]);

  const loadOptionGroups = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const groups = await menuService.getOptionGroups(productId);
      setOptionGroups(groups);
      setExpandedGroups(new Set(groups.map((g) => g.id)));
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Seçenek grupları yüklenirken hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleGroupExpanded = (groupId: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  const handleCreateGroup = async (formData: any) => {
    try {
      await menuService.createOptionGroup(productId, formData);
      await loadOptionGroups();
      setShowCreateForm(false);
      onUpdate?.();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Grup oluşturulamadı');
      throw err;
    }
  };

  const handleUpdateGroup = async (groupId: string, formData: any) => {
    try {
      await menuService.updateOptionGroup(groupId, formData);
      await loadOptionGroups();
      setEditingGroupId(null);
      onUpdate?.();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Grup güncellenemedi');
      throw err;
    }
  };

  const handleDeleteGroup = async (groupId: string) => {
    if (!confirm('Bu seçenek grubunu silmek istediğinize emin misiniz? Tüm seçenekler de silinecektir.')) {
      return;
    }

    try {
      await menuService.deleteOptionGroup(groupId);
      await loadOptionGroups();
      onUpdate?.();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Grup silinemedi');
    }
  };

  const handleCreateOption = async (optionGroupId: string, formData: any) => {
    try {
      await menuService.createOption(optionGroupId, {
        ...formData,
        priceModifier: parseDecimal(formData.priceModifier),
      });
      await loadOptionGroups();
      setShowOptionForm(null);
      onUpdate?.();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Seçenek oluşturulamadı');
      throw err;
    }
  };

  const handleUpdateOption = async (optionId: string, formData: any) => {
    try {
      await menuService.updateOption(optionId, {
        ...formData,
        priceModifier: parseDecimal(formData.priceModifier),
      });
      await loadOptionGroups();
      setEditingOptionId(null);
      onUpdate?.();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Seçenek güncellenemedi');
      throw err;
    }
  };

  const handleDeleteOption = async (optionId: string) => {
    if (!confirm('Bu seçeneği silmek istediğinize emin misiniz?')) {
      return;
    }

    try {
      await menuService.deleteOption(optionId);
      await loadOptionGroups();
      onUpdate?.();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Seçenek silinemedi');
    }
  };

  if (isLoading) {
    return (
      <Card>
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-gray-600">Yükleniyor...</p>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold text-gray-900">Porsiyon ve Özelleştirme Seçenekleri</h3>
          <p className="text-sm text-gray-600">
            Bu ürüne özel seçenekler (ör: Porsiyon: Tam/Çift, Boyut: Küçük/Orta/Büyük)
          </p>
        </div>
        <Button
          variant="primary"
          size="sm"
          onClick={() => setShowCreateForm(!showCreateForm)}
        >
          {showCreateForm ? '✕ İptal' : '+ Yeni Grup'}
        </Button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-error/10 border border-error/20 rounded-lg">
          <p className="text-error text-sm">{error}</p>
          <Button size="sm" variant="secondary" onClick={() => setError(null)} className="mt-2">
            Kapat
          </Button>
        </div>
      )}

      {showCreateForm && (
        <OptionGroupForm
          onSubmit={handleCreateGroup}
          onCancel={() => setShowCreateForm(false)}
          onAlert={(title, message, variant) => setAlertState({ isOpen: true, title, message, variant })}
        />
      )}

      <div className="space-y-3">
        {optionGroups.length === 0 ? (
          <div className="text-center py-8 text-gray-400 border-2 border-dashed border-gray-200 rounded-lg">
            <p className="text-sm mb-2">Henüz seçenek grubu yok</p>
            <p className="text-xs">Örn: Porsiyon (Tam/Çift), Boyut (Küçük/Orta/Büyük)</p>
            <p className="text-xs mt-2 text-gray-500">
              💡 Not: Yan ürünler ve ekstralar için "Yan Ürün Grupları" bölümünü kullanın
            </p>
          </div>
        ) : (
          optionGroups.map((group) => (
            <OptionGroupCard
              key={group.id}
              group={group}
              isExpanded={expandedGroups.has(group.id)}
              onToggleExpanded={() => toggleGroupExpanded(group.id)}
              isEditing={editingGroupId === group.id}
              onStartEdit={() => setEditingGroupId(group.id)}
              onCancelEdit={() => setEditingGroupId(null)}
              onSave={(formData) => handleUpdateGroup(group.id, formData)}
              onDelete={() => handleDeleteGroup(group.id)}
              showOptionForm={showOptionForm === group.id}
              onShowOptionForm={() => setShowOptionForm(group.id)}
              onHideOptionForm={() => setShowOptionForm(null)}
              onCreateOption={(formData) => handleCreateOption(group.id, formData)}
              editingOptionId={editingOptionId}
              onStartEditOption={setEditingOptionId}
              onCancelEditOption={() => setEditingOptionId(null)}
              onUpdateOption={handleUpdateOption}
              onDeleteOption={handleDeleteOption}
              onAlert={(title, message, variant) => setAlertState({ isOpen: true, title, message, variant })}
            />
          ))
        )}
      </div>

      <AlertModal
        isOpen={alertState.isOpen}
        title={alertState.title}
        message={alertState.message}
        variant={alertState.variant}
        onClose={() => setAlertState((a) => ({ ...a, isOpen: false }))}
      />
    </Card>
  );
};

interface OptionGroupFormProps {
  onSubmit: (formData: any) => Promise<void>;
  onCancel: () => void;
  initialData?: any;
  onAlert?: (title: string, message: string, variant: AlertModalVariant) => void;
}

const OptionGroupForm = ({ onSubmit, onCancel, initialData, onAlert }: OptionGroupFormProps) => {
  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    description: initialData?.description || '',
    minSelect: initialData?.minSelect ?? 0,
    maxSelect: initialData?.maxSelect ?? null as number | null,
    isRequired: initialData?.isRequired || false,
    displayOrder: initialData?.displayOrder || 0,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      onAlert?.('Eksik bilgi', 'Grup adı zorunludur', 'warning');
      return;
    }
    if (formData.isRequired && formData.minSelect < 1) {
      onAlert?.('Geçersiz ayar', 'Zorunlu gruplar için minimum seçim en az 1 olmalıdır', 'warning');
      return;
    }
    if (formData.maxSelect !== null && formData.maxSelect < formData.minSelect) {
      onAlert?.('Geçersiz ayar', 'Maksimum seçim minimum seçimden küçük olamaz', 'warning');
      return;
    }
    setIsSubmitting(true);
    try {
      await onSubmit(formData);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="mb-4 p-4 bg-gray-50 rounded-lg border-2 border-primary/20">
      <form onSubmit={handleSubmit} className="space-y-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Grup Adı *
          </label>
          <Input
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Örn: Porsiyon, Yan Ürünler"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Açıklama
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
            rows={2}
            placeholder="Grup açıklaması (isteğe bağlı)"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Minimum Seçim
            </label>
            <NumberInput
              integerOnly
              value={formData.minSelect === 0 ? '' : String(formData.minSelect)}
              onChange={(s) =>
                setFormData({ ...formData, minSelect: s === '' ? 0 : Math.max(0, parseInt(s, 10) || 0) })
              }
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Maksimum Seçim (boş = sınırsız)
            </label>
            <NumberInput
              integerOnly
              value={formData.maxSelect != null ? String(formData.maxSelect) : ''}
              onChange={(s) =>
                setFormData({
                  ...formData,
                  maxSelect: s === '' ? null : Math.max(0, parseInt(s, 10) || 0),
                })
              }
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={formData.isRequired}
            onChange={(e) => setFormData({ ...formData, isRequired: e.target.checked })}
            className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
          />
          <label className="text-sm font-medium text-gray-700">
            Zorunlu (müşteri seçim yapmalı)
          </label>
        </div>

        <div className="flex gap-2 justify-end">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={onCancel}
          >
            İptal
          </Button>
          <Button
            type="submit"
            variant="primary"
            size="sm"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Kaydediliyor...' : 'Kaydet'}
          </Button>
        </div>
      </form>
    </div>
  );
};

interface OptionGroupCardProps {
  group: any;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  isEditing: boolean;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSave: (formData: any) => Promise<void>;
  onDelete: () => void;
  showOptionForm: boolean;
  onShowOptionForm: () => void;
  onHideOptionForm: () => void;
  onCreateOption: (formData: any) => Promise<void>;
  editingOptionId: string | null;
  onStartEditOption: (id: string) => void;
  onCancelEditOption: () => void;
  onUpdateOption: (id: string, formData: any) => Promise<void>;
  onDeleteOption: (id: string) => void;
  onAlert?: (title: string, message: string, variant: AlertModalVariant) => void;
}

const OptionGroupCard = ({
  group,
  isExpanded,
  onToggleExpanded,
  isEditing,
  onStartEdit,
  onCancelEdit,
  onSave,
  onDelete,
  showOptionForm,
  onShowOptionForm,
  onHideOptionForm,
  onCreateOption,
  editingOptionId,
  onStartEditOption,
  onCancelEditOption,
  onUpdateOption,
  onDeleteOption,
  onAlert,
}: OptionGroupCardProps) => {
  if (isEditing) {
    return (
      <Card className="border-2 border-primary">
        <OptionGroupForm
          initialData={group}
          onSubmit={onSave}
          onCancel={onCancelEdit}
          onAlert={onAlert}
        />
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden">
      <div
        className={cn(
          'transition-all duration-200',
          isExpanded ? 'border-b border-gray-200 pb-3 mb-3' : '',
        )}
      >
        <div className="flex items-center justify-between">
          <button
            onClick={onToggleExpanded}
            className="flex-1 flex items-center gap-3 text-left hover:opacity-80 transition-opacity group"
          >
            <span className="text-sm transition-transform duration-200">
              {isExpanded ? '▼' : '▶'}
            </span>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h4 className="font-bold text-gray-900 group-hover:text-primary transition-colors">
                  {group.name}
                </h4>
                {group.isRequired && (
                  <Badge variant="primary" className="text-xs">
                    Zorunlu
                  </Badge>
                )}
                <Badge variant="secondary" className="text-xs">
                  {group.options?.length || 0} seçenek
                </Badge>
              </div>
              {group.description && (
                <p className="text-xs text-gray-500 mt-1">{group.description}</p>
              )}
              <div className="flex items-center gap-3 mt-1 text-xs text-gray-600">
                <span>
                  Min: {group.minSelect} • Max: {group.maxSelect ?? '∞'}
                </span>
              </div>
            </div>
          </button>

          <div className="flex items-center gap-1.5 ml-4">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onStartEdit();
              }}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Düzenle"
            >
              <span className="text-gray-600 hover:text-primary">✏️</span>
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              className="p-2 hover:bg-error/10 rounded-lg transition-colors"
              title="Sil"
            >
              <span className="text-error">🗑️</span>
            </button>
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h5 className="text-sm font-semibold text-gray-700">Seçenekler</h5>
            <Button
              variant="secondary"
              size="sm"
              onClick={showOptionForm ? onHideOptionForm : onShowOptionForm}
            >
              {showOptionForm ? '✕ İptal' : '+ Seçenek Ekle'}
            </Button>
          </div>

          {showOptionForm && (
            <OptionForm
              onSubmit={onCreateOption}
              onCancel={onHideOptionForm}
              onValidationError={(message) => onAlert?.('Eksik bilgi', message, 'warning')}
            />
          )}

          <div className="space-y-2">
            {group.options && group.options.length > 0 ? (
              group.options.map((option: any) => (
                <OptionItem
                  key={option.id}
                  option={option}
                  isEditing={editingOptionId === option.id}
                  onStartEdit={() => onStartEditOption(option.id)}
                  onCancelEdit={onCancelEditOption}
                  onSave={(formData) => onUpdateOption(option.id, formData)}
                  onDelete={() => onDeleteOption(option.id)}
                  onValidationError={(message) => onAlert?.('Eksik bilgi', message, 'warning')}
                />
              ))
            ) : (
              <div className="text-center py-4 text-gray-400 text-sm border-2 border-dashed border-gray-200 rounded-lg">
                Henüz seçenek yok
              </div>
            )}
          </div>
        </div>
      )}
    </Card>
  );
};

interface OptionFormProps {
  onSubmit: (formData: any) => Promise<void>;
  onCancel: () => void;
  initialData?: any;
  onValidationError?: (message: string) => void;
}

const OptionForm = ({ onSubmit, onCancel, initialData, onValidationError }: OptionFormProps) => {
  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    description: initialData?.description || '',
    priceModifier: initialData?.priceModifier != null ? String(initialData.priceModifier) : '',
    displayOrder: initialData?.displayOrder || 0,
    isDefault: initialData?.isDefault || false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      onValidationError?.('Seçenek adı zorunludur.');
      return;
    }
    setIsSubmitting(true);
    try {
      await onSubmit(formData);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="p-3 bg-white border-2 border-primary/20 rounded-lg">
      <form onSubmit={handleSubmit} className="space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Seçenek Adı *
            </label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Örn: Tam Porsiyon"
              required
              size="sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Fiyat Düzenlemesi (₺)
            </label>
            <NumberInput
              value={formData.priceModifier}
              onChange={(s) => setFormData({ ...formData, priceModifier: s })}
              placeholder="0,00"
              className="py-2 text-sm"
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Açıklama
          </label>
          <Input
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Seçenek açıklaması (isteğe bağlı)"
            size="sm"
          />
        </div>

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={formData.isDefault}
            onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
            className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
          />
          <label className="text-xs text-gray-700">Varsayılan seçim</label>
        </div>

        <div className="flex gap-2 justify-end">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={onCancel}
          >
            İptal
          </Button>
          <Button
            type="submit"
            variant="primary"
            size="sm"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Kaydediliyor...' : 'Kaydet'}
          </Button>
        </div>
      </form>
    </div>
  );
};

interface OptionItemProps {
  option: any;
  isEditing: boolean;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSave: (formData: any) => Promise<void>;
  onDelete: () => void;
  onValidationError?: (message: string) => void;
}

const OptionItem = ({
  option,
  isEditing,
  onStartEdit,
  onCancelEdit,
  onSave,
  onDelete,
  onValidationError,
}: OptionItemProps) => {
  if (isEditing) {
    return (
      <div className="p-3 bg-white border-2 border-primary rounded-lg">
        <OptionForm
          initialData={option}
          onSubmit={onSave}
          onCancel={onCancelEdit}
          onValidationError={onValidationError}
        />
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors">
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-900">{option.name}</span>
          {option.isDefault && (
            <Badge variant="info" className="text-xs">
              Varsayılan
            </Badge>
          )}
          {!option.isActive && (
            <Badge variant="danger" className="text-xs">
              Pasif
            </Badge>
          )}
        </div>
        {option.description && (
          <p className="text-xs text-gray-500 mt-1">{option.description}</p>
        )}
      </div>
      <div className="flex items-center gap-3">
        <span className="text-sm font-bold text-primary">
          {option.priceModifier > 0 ? '+' : ''}
          {formatCurrency(option.priceModifier)}
        </span>
        <button
          onClick={onStartEdit}
          className="p-1.5 hover:bg-gray-200 rounded transition-colors"
          title="Düzenle"
        >
          <span className="text-gray-600 text-sm">✏️</span>
        </button>
        <button
          onClick={onDelete}
          className="p-1.5 hover:bg-error/10 rounded transition-colors"
          title="Sil"
        >
          <span className="text-error text-sm">🗑️</span>
        </button>
      </div>
    </div>
  );
};
